import {createAsyncThunk, createSelector, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState, StoreState} from "app/realtime-store/redux-store";
import PaginatedResponse from "common/contracts/paginated-response";
import Guid from "common/values/guid/guid";
import MessagingAPIService, {AttachmentQuery} from "messaging/api/messaging-api-service";
import Forum from "messaging/entities/forum/forum";
import MessageFileInfo from "messaging/values/attachment/message-file-info";
import {useSelector} from "react-redux";
import Session from "users/session/session";

const initialState: StoreState<PaginatedResponse<MessageFileInfo>> = {
  entries: {},
  loading: {},
  error: {},
};

export const populateForumAttachments = createAsyncThunk(
  "messages/getAttachmentsByForum",
  async (
    {
      session,
      forum,
      query
    }: {
      session: Session;
      forum: Forum,
      query: AttachmentQuery,
      append: boolean
    },
    thunkAPI
  ) => {
    try {
      const apiService = new MessagingAPIService(session);
      return await apiService.getAttachmentsByForum(
        forum,
        query
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const attachmentsSlice = createSlice({
  name: "attachments",
  initialState,
  reducers: {
    addAttachment: (state, action: PayloadAction<{ forumId: Guid; attachment: MessageFileInfo }>) => {
      const { forumId, attachment } = action.payload;
      if (!forumId) return;

      let existingForumAttachments = state.entries[forumId.value];
      existingForumAttachments = existingForumAttachments.appendOrReplaceData(
        [attachment]
      );
      state.entries[forumId.value] = existingForumAttachments;
    },
    removeAttachment: (state, action: PayloadAction<Guid>) => {
      Object.entries(state.entries).forEach(([forumId, forumAttachments]) => {
        if (!forumAttachments) return;
        state.entries[forumId] = forumAttachments.removeDataById(action.payload);
      });
    }
  },
  extraReducers: (builder) => {
    builder.addCase(populateForumAttachments.pending, (state, action) => {
      const forumId = action.meta.arg.forum.id?.value;
      if (!forumId) return;

      state.loading[forumId] = true;
      state.error[forumId] = null;
    });
    builder.addCase(populateForumAttachments.fulfilled, (state, action) => {
      const forumId = action.meta.arg.forum.id?.value;
      if (!forumId) return;

      let existingForumAttachments = state.entries[forumId] ?? PaginatedResponse.empty();
      if (!existingForumAttachments.isEmpty && action.meta.arg.append) {
        existingForumAttachments = existingForumAttachments.appendOrReplaceData(
          action.payload.data,
          action.payload.headers,
          (a, b) => a.created.diff(b.created)
        );
        state.entries[forumId] = existingForumAttachments;
      } else {
        state.entries[forumId] = action.payload;
      }
      state.loading[forumId] = false;
    });
    builder.addCase(populateForumAttachments.rejected, (state, action) => {
      const forumId = action.meta.arg.forum.id?.value;
      if (!forumId) return;

      state.loading[forumId] = false;
      state.error[forumId] = action.error;
    });
  },
});

const selectEntries = (state: RootState) => state.attachments.entries;

const selectAttachmentsByForumId = createSelector(
  [selectEntries, (_: RootState, forumId?: Guid) => forumId?.value ?? ""],
  (entries, id) => entries[id] ?? []
);

export const { addAttachment, removeAttachment } = attachmentsSlice.actions;

export const getIsLoadingAttachmentsByForumId = (forumId?: Guid) =>
  useSelector((state: RootState) => {
    if (!forumId) return false;
    return state.attachments?.loading[forumId.value]
  });
export const getAttachmentsByForumId = (forumId?: Guid) =>
  useSelector((state: RootState) =>
    selectAttachmentsByForumId(state, forumId)
  );
export const getErrorLoadingAttachmentsByForumId = (forumId: Guid) =>
  useSelector(
    (state: RootState) => state.attachments.error[forumId?.value ?? ""]
  );

export default attachmentsSlice;
